import React from 'react';
import { FaComment } from 'react-icons/fa';
import { UncontrolledTooltip } from 'reactstrap';
import styled from 'styled-components';
import { getTranslationKey } from '../../modules/utils';
import { countFontRatio, useCommentCountBadge } from './hooks/useCommentCountBadge';

export { formatCommentCount } from './hooks/useCommentCountBadge';

const Badge = styled.span`
  position: relative;
  display: inline-flex;
  cursor: pointer;

  svg {
    position: initial;
    /* Mirrored so the bubble's tail points down into the corner of the tile. */
    transform: scaleX(-1);
    /* Keeps the white bubble readable on a light photo. */
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
  }
`;

// `$`-prefixed props are transient - styled-components v5.1+ keeps them out of
// the DOM, which matters because `label` is a real HTML attribute.
const Count = styled.span<{ $label?: string; $size: number }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  /*
   * FaComment's 512x512 viewBox puts the bubble body's centre at ~(256, 240) and
   * hangs the tail below it, down to y=480. Centring on the icon box would sit a
   * touch low, so trim the tail's share off the bottom to land on the bubble.
   */
  padding-bottom: ${({ $size }) => $size * 0.08}px;
  color: #4a4a4a;
  font-size: ${({ $label, $size }) => $size * countFontRatio($label)}px;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
`;

interface IProps {
  imageId: string;
  commentCount: number;
  onClick: (imageId: string) => void;
  size?: number;
  /**
   * The grid behind the fullscreen overlay stays mounted and renders its own
   * badge for the same image, so the tooltip target has to differ - otherwise
   * both tooltips bind to both badges and open at once.
   */
  targetIdPrefix?: string;
}

/**
 * White chat bubble with the comment count inside it. Opens the comment history
 * for one image.
 */
export const CommentCountBadge = ({
  imageId,
  commentCount,
  onClick,
  size = 26,
  targetIdPrefix = 'image_comments'
}: IProps) => {
  const { displayedCount, targetId, open, onKeyDown } = useCommentCountBadge({
    imageId,
    commentCount,
    onClick,
    targetIdPrefix
  });

  return (
    <>
      <Badge
        id={targetId}
        role="button"
        tabIndex={0}
        data-cy={`image-comments-${imageId}`}
        onClick={open}
        onKeyDown={onKeyDown}
      >
        <FaComment color="#fff" size={size} />
        <Count $label={displayedCount} $size={size}>
          {displayedCount}
        </Count>
      </Badge>
      <UncontrolledTooltip placement="bottom" target={targetId}>
        {getTranslationKey('collectionSettings.comments.badgeTooltip', { count: commentCount })}
      </UncontrolledTooltip>
    </>
  );
};
